import { container } from 'tsyringe'
import Vue from 'vue'
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators'
import store from '@/app/ui/store'
import { EventBus, EventBusConstants, Utils } from '@/app/infrastructures/misc'
import { RequestTopupSaldoPresenter } from '@/app/ui/presenters/RequestTopupSaldoPresenter'
import { Pagination } from '@/domain/entities/Pagination'
import { REQUEST_TOP_SALDO_PAGINATION } from '@/app/infrastructures/misc'
import {
  DetailTopupSaldo,
  DetailTopupSaldoForm,
  RequestTopupSaldoData,
  RequestTopupSaldoHistory,
  RequestTopupSaldoList
} from '@/domain/entities/RequestTopupSaldo'
import { ApprovingTopupRequest, RequestTopupRequest } from '@/data/payload/api/AdjustmentRequest'
import { EnumTypeBalanceStatus } from '@/app/infrastructures/misc/Constants/adjustment'

export interface RequestTopupListParams {
  page: number
  perPage: number
  balanceStatus?: string
  filterStatus?: string
  phoneNumber?: string
  balanceType?: string
  sortBy?: string
  customerId?: number
}

export interface RequestTopupSaldoState {
  isLoading: boolean
  listRequestTopup: RequestTopupSaldoData[]
  paginationData: Pagination
}

export interface RequestTopupFormState {
  customerId: number,
  amount: number,
  note: string,
}

export interface ApprovingTopupFormState {
  balanceId: number,
  balanceStatus: EnumTypeBalanceStatus,
  reasonNote: string
}

@Module({ namespaced: true, store, name: 'adjustment-request-topup', dynamic: true })
class RequestTopupSaldoController extends VuexModule implements RequestTopupSaldoState {
  private presenter: RequestTopupSaldoPresenter = container.resolve(RequestTopupSaldoPresenter)
  public isLoading = false
  public listRequestTopup = [new RequestTopupSaldoData()]
  public listRequestTopupHistory = [new RequestTopupSaldoHistory()]
  public paginationData = new Pagination(1, REQUEST_TOP_SALDO_PAGINATION, 0)
  public detailTopupForm = new DetailTopupSaldo()
  public phoneNumber = ''

  @Action({ rawError: true })
  public getListRequestTopupSaldo(params: RequestTopupListParams): void {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getAll(formattedParams)
      .then((res) => {
        this.setListRequestTopup(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch List Request Topup Failed',
          text:
            [400, 422].includes(error.status)
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getHistoryRequestTop(params: RequestTopupListParams): void {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
    .getHistory(formattedParams)
    .then((res) => {
      this.setListRequestTopupHistory(res)
    })
    .catch(error => {
      Vue.notify({
        title: 'Fetch List History Request Topup Failed',
        text:
          [400, 422].includes(error.status)
            ? error.error.message.en
            : 'Something wrong',
        type: 'error',
        duration: 5000,
      })
    })
    .finally(() => {
      this.setLoading(false)
    })
  }

  @Action({ rawError: true })
  public createRequestTopup(form: RequestTopupFormState): void {
    this.setLoading(true)
    this.presenter
      .postRequestTopup(new RequestTopupRequest(
        form.customerId,
        form.amount,
        form.note
      ))
      .then(() => {
        EventBus.$emit(EventBusConstants.CREATE_REQUEST_TOPUP_SUCCESS)
      })
      .catch((error) => {
        Vue.notify({
          title: 'Create Public Voucher Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getDetailTopupForm(customerId: number): void {
    this.setLoading(true)
    
    this.presenter
      .getDetailTopup(customerId)
      .then(res => {
        this.setDetailTopupForm(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch List Failed',
          text:
            [400, 422].includes(error.status)
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public approvingTopupSaldo(form: ApprovingTopupFormState): void {
    this.setLoading(true)
    this.presenter
      .postApprovingTopup(new ApprovingTopupRequest(
        form.balanceId,
        form.balanceStatus,
        form.reasonNote
      ))
      .then(() => {
        if (form.balanceStatus === EnumTypeBalanceStatus.APPROVED) {
          EventBus.$emit(EventBusConstants.APPROVE_APPROVING_TOPUP_SUCCESS)
        } else {
          EventBus.$emit(EventBusConstants.REJECT_APPROVING_TOPUP_SUCCESS)
        }
      })
      .catch((error: { status: number; error: { message: { en: string | undefined } } }) => {
        if (form.balanceStatus === EnumTypeBalanceStatus.APPROVED) {
          EventBus.$emit(EventBusConstants.APPROVE_APPROVING_TOPUP_FAILED)
        } else {
          EventBus.$emit(EventBusConstants.REJECT_APPROVING_TOPUP_FAILED)
        }
        Vue.notify({
          title: `Topup Saldo Failed To ${form.balanceStatus}`,
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
            type: 'error',
            duration: 5000
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Mutation
  private setLoading(bool: boolean): void {
    this.isLoading = bool
  }

  @Mutation
  private setListRequestTopup(res: RequestTopupSaldoList): void {
    this.listRequestTopup = <RequestTopupSaldoData[]>res.data
    this.paginationData = <Pagination>res.pagination
  }

  @Mutation
  private setListRequestTopupHistory(res: RequestTopupSaldoList): void {
    this.listRequestTopupHistory = <RequestTopupSaldoData[]>res.data
    this.paginationData = <Pagination>res.pagination
    this.phoneNumber = <string>res.phoneNumber
  }

  @Mutation
  private setDetailTopupForm(res: DetailTopupSaldoForm): void {
    this.detailTopupForm = <DetailTopupSaldo>res.data
  }
}

export default getModule(RequestTopupSaldoController)
